import { Injectable, ComponentFactoryResolver, ChangeDetectorRef } from '@angular/core';
import { Injector, ComponentRef, ViewContainerRef, TemplateRef } from '@angular/core';
import { TemplatePortal, Overlay, OverlayState, OverlayRef, OverlayOrigin, ComponentPortal } from '@angular/material';
import { Subject } from 'rxjs/Subject';
import { Observable } from 'rxjs/Observable';
import { Subscription } from 'rxjs/Subscription';

import { TdLoadingContext } from '../directives/loading.directive';
import { TdLoadingComponent, LoadingType, LoadingMode, LoadingStrategy, LoadingStyle } from '../loading.component';
import { ITdLoadingConfig} from './loading.service';

export interface IInternalLoadingOptions extends ITdLoadingConfig {
  height?: number;
  style?: LoadingStyle;
}

export interface ILoadingRef {
  observable: Observable<any>;
  componentRef: ComponentRef<any>;
  subject?: Subject<any>;
  times?: number;
}
/**
 * NOTE: \@internal usage only.
 */
export class TdLoadingFactory {
/**
 * @param {?} _componentFactoryResolver
 * @param {?} _overlay
 * @param {?} _injector
 */
constructor(private _componentFactoryResolver: ComponentFactoryResolver,
private _overlay: Overlay,
private _injector: Injector) {
  }
/**
 * Uses material `Overlay` services to create a DOM element and attach the loading component
 * into it. Leveraging the state and configuration from it.
 * 
 * Saves a reference in context to be called when registering/resolving the loading element.
 * @param {?} options
 * @return {?}
 */
public createFullScreenComponent(options: ITdLoadingConfig): ILoadingRef {
    ( /** @type {?} */((<IInternalLoadingOptions>options))).height = undefined;
    ( /** @type {?} */((<IInternalLoadingOptions>options))).style = LoadingStyle.FullScreen;
    let /** @type {?} */ loadingRef: ILoadingRef = this._initializeContext();
    let /** @type {?} */ loading: boolean = false;
    let /** @type {?} */ overlayRef: OverlayRef;
    loadingRef.observable
    .subscribe((registered: number) => {
      if (registered > 0 && !loading) {
        loading = true;
        overlayRef = this._createOverlay();
        loadingRef.componentRef = overlayRef.attach(new ComponentPortal(TdLoadingComponent));
        this._mapOptions(options, loadingRef.componentRef.instance);
        loadingRef.componentRef.instance.startInAnimation();
      } else if (registered <= 0 && loading) {
        loading = false;
        let /** @type {?} */ subs: Subscription = loadingRef.componentRef.instance.startOutAnimation().subscribe(() => {
          subs.unsubscribe();
          loadingRef.componentRef.destroy();
          overlayRef.detach();
          overlayRef.dispose();
        });
      }
    });
    return loadingRef;
  }
/**
 * Creates a loading component dynamically and attaches it into the given viewContainerRef.
 * Leverages TemplatePortals from material to inject the template inside of it so it fits
 * perfecly when overlaying it.
 * 
 * Saves a reference in context to be called when registering/resolving the loading element.
 * @param {?} options
 * @param {?} viewContainerRef
 * @param {?} templateRef
 * @return {?}
 */
public createOverlayComponent(options: ITdLoadingConfig, viewContainerRef: ViewContainerRef,
                                templateRef: TemplateRef<Object>): ILoadingRef {
    ( /** @type {?} */((<IInternalLoadingOptions>options))).height = undefined;
    ( /** @type {?} */((<IInternalLoadingOptions>options))).style = LoadingStyle.Overlay;
    let /** @type {?} */ loadingRef: ILoadingRef = this._createComponent(options);
    let /** @type {?} */ loading: boolean = false;
    loadingRef.componentRef.instance.content = new TemplatePortal(templateRef, viewContainerRef);
    viewContainerRef.clear();
    viewContainerRef.insert(loadingRef.componentRef.hostView, 0);
    loadingRef.observable
    .subscribe((registered: number) => {
      if (registered > 0 && !loading) {
        loading = true;
        loadingRef.componentRef.instance.startInAnimation();
      } else if (registered <= 0 && loading) {
        loading = false;
        loadingRef.componentRef.instance.startOutAnimation();
      }
    });
    return loadingRef;
  }
/**
 * Creates a loading component dynamically and attaches it into the given viewContainerRef.
 * Replaces the template with the loading component depending if it was registered or resolved.
 * 
 * Saves a reference in context to be called when registering/resolving the loading element.
 * @param {?} options
 * @param {?} viewContainerRef
 * @param {?} templateRef
 * @param {?} context
 * @return {?}
 */
public createReplaceComponent(options: ITdLoadingConfig, viewContainerRef: ViewContainerRef,
                                templateRef: TemplateRef<Object>, context: TdLoadingContext): ILoadingRef {
    let /** @type {?} */ nativeElement: HTMLElement = /** @type {?} */(( <HTMLElement>templateRef.elementRef.nativeElement));
    ( /** @type {?} */((<IInternalLoadingOptions>options))).height = nativeElement.nextElementSibling ?
      nativeElement.nextElementSibling.scrollHeight : undefined;
    ( /** @type {?} */((<IInternalLoadingOptions>options))).style = LoadingStyle.None;
    let /** @type {?} */ loadingRef: ILoadingRef = this._createComponent(options);
    let /** @type {?} */ loading: boolean = false;
    viewContainerRef.createEmbeddedView(templateRef, context);
    loadingRef.observable
    .subscribe((registered: number) => {
      if (registered > 0 && !loading) {
        loading = true;
        let /** @type {?} */ index: number = viewContainerRef.indexOf(loadingRef.componentRef.hostView);
        if (index < 0) {
          viewContainerRef.clear();
          viewContainerRef.insert(loadingRef.componentRef.hostView, 0);
        }
        loadingRef.componentRef.instance.startInAnimation();
      } else if (registered <= 0 && loading) {
        loading = false;
        let /** @type {?} */ subs: Subscription = loadingRef.componentRef.instance.startOutAnimation().subscribe(() => {
          subs.unsubscribe();
          // passing context so when the template is re-attached, we can keep the reference of the variables
          let /** @type {?} */ cdr: ChangeDetectorRef = viewContainerRef.createEmbeddedView(templateRef, context);
          viewContainerRef.detach(viewContainerRef.indexOf(loadingRef.componentRef.hostView));
          /**
           * Need to call "markForCheck" and "detectChanges" on attached template, so its detected by parent component when attached
           * with "OnPush" change detection
           */
          cdr.detectChanges();
          cdr.markForCheck();
        });
      }
    });
    return loadingRef;
  }
/**
 * Creates a fullscreen overlay for the loading usage.
 * @return {?}
 */
private _createOverlay(): OverlayRef {
    let /** @type {?} */ state: OverlayState = new OverlayState();
    state.hasBackdrop = false;
    state.positionStrategy = this._overlay.position().global().centerHorizontally().centerVertically();
    return this._overlay.create(state);
  }
/**
 * Creates a generic component dynamically waiting to be attached to a viewContainerRef.
 * @param {?} options
 * @return {?}
 */
private _createComponent(options: IInternalLoadingOptions): ILoadingRef {
    let /** @type {?} */ compRef: ILoadingRef = this._initializeContext();
    compRef.componentRef = this._componentFactoryResolver
    .resolveComponentFactory(TdLoadingComponent).create(this._injector);
    this._mapOptions(options, compRef.componentRef.instance);
    return compRef;
  }
/**
 * Initialize context for loading component.
 * @return {?}
 */
private _initializeContext(): ILoadingRef {
    let /** @type {?} */ subject: Subject<any> = new Subject<any>();
    return {
      observable: subject.asObservable(),
      subject: subject,
      componentRef: undefined,
      times: 0,
    };
  }
/**
 * Maps configuration to the loading component instance.
 * @param {?} options
 * @param {?} instance
 * @return {?}
 */
private _mapOptions(options: IInternalLoadingOptions, instance: TdLoadingComponent): void {
    instance.style = options.style;
    if (options.type !== undefined) {
      instance.type = options.type;
    }
    if (options.height !== undefined) {
      instance.height = options.height;
    }
    if (options.mode !== undefined) {
      instance.mode = options.mode;
    }
    if (options.color !== undefined) {
      instance.color = options.color;
    }
  }
static decorators: DecoratorInvocation[] = [
{ type: Injectable },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: ComponentFactoryResolver, },
{type: Overlay, },
{type: Injector, },
];
}

function TdLoadingFactory_tsickle_Closure_declarations() {
/** @type {?} */
TdLoadingFactory.decorators;
/**
 * @nocollapse
 * @type {?}
 */
TdLoadingFactory.ctorParameters;
/** @type {?} */
TdLoadingFactory.prototype._componentFactoryResolver;
/** @type {?} */
TdLoadingFactory.prototype._overlay;
/** @type {?} */
TdLoadingFactory.prototype._injector;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
